import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Check, Moon, Clock } from 'lucide-react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Label } from './ui/label';

const MEDITACIONES_PACKAGES = [
  {
    id: null,
    name: 'Sin Meditaciones',
    price: 0,
    description: 'No incluir servicio de meditaciones guiadas',
    features: []
  },
  {
    id: 'med_essential',
    name: 'Essential',
    price: 1997,
    description: 'Grabación de meditación guiada con edición de música de fondo (proporcionada por cliente)',
    features: [
      'Grabación profesional de voz',
      'Edición de música de fondo relajante'
    ]
  },
  {
    id: 'med_platino',
    name: 'Platino',
    price: 3997,
    description: 'Meditación guiada con música de librería (6 piezas máximo)',
    features: [
      'Grabación profesional de voz',
      'Música de librería (6 pz)'
    ]
  },
  {
    id: 'med_premium',
    name: 'Premium',
    price: 6997,
    description: 'Experiencia completa de meditación con entregable en audio y video',
    features: [
      'Grabación de voz',
      'Video de stock / efectos visuales',
      '10 min de música original',
      'Diseño sonoro binaural'
    ]
  }
];

const MEDITACIONES_ADDONS = [
  {
    id: 'med_musica_original',
    name: 'Música Original Exclusiva',
    price: 16997,
    perProgram: false
  },
  {
    id: 'med_efectos_binaurales',
    name: 'Efectos Binaurales',
    price: 4997,
    perProgram: false
  }
];

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-700">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-semibold text-lg">{title}</span>
          {badge && (
            <span className="px-2 py-1 bg-[#aa2aff]/20 text-[#aa2aff] text-sm rounded-full font-medium">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-[#080c2d]/50 border-t border-white/10">
          {children}
        </div>
      )}
    </div>
  );
}

function PackageCard({ pkg, isSelected, onSelect, formatPrice, currency }) {
  return (
    <button
      onClick={() => onSelect(pkg.id)}
      className={cn(
        "flex flex-col p-3 rounded-xl border text-left transition-all h-full min-h-[180px]",
        isSelected
          ? "border-[#aa2aff] bg-[#aa2aff]/10 shadow-[0_0_15px_rgba(170,42,255,0.2)]"
          : "border-white/10 hover:border-white/20 bg-white/5"
      )}
    >
      <div className="flex flex-col gap-1 mb-2">
        <h4 className={cn(
          "font-semibold text-sm",
          isSelected ? "text-[#aa2aff]" : "text-white"
        )}>
          {pkg.name}
        </h4>
        <span className="text-white font-bold text-xs">
          {pkg.price > 0 ? `${formatPrice(pkg.price)} ${currency}/hr` : 'Gratis'}
        </span>
      </div>
      <p className="text-slate-400 text-xs mb-2 line-clamp-2">{pkg.description}</p>
      {pkg.features.length > 0 && (
        <ul className="space-y-0.5 mt-auto">
          {pkg.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-1 text-[10px] text-slate-300">
              <Check className="w-3 h-3 text-[#aa2aff] flex-shrink-0 mt-0.5" />
              <span className="line-clamp-1">{feature}</span>
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}

export function MeditacionesSection({
  isOpen,
  onToggle,
  selectedPackage,
  onPackageSelect,
  selectedAddons = [],
  onAddonToggle,
  meditacionesQuantity = 1,
  onQuantityChange,
  meditacionesData = {},
  onMeditacionesDataChange,
  formatPrice,
  currency = 'MXN',
  servicePrices = {}
}) {
  const meditacionesPackages = [
    { ...MEDITACIONES_PACKAGES[0] },
    {
      ...MEDITACIONES_PACKAGES[1],
      price: servicePrices.price_med_essential || MEDITACIONES_PACKAGES[1].price
    },
    {
      ...MEDITACIONES_PACKAGES[2],
      price: servicePrices.price_med_platino || MEDITACIONES_PACKAGES[2].price
    },
    {
      ...MEDITACIONES_PACKAGES[3],
      price: servicePrices.price_med_premium || MEDITACIONES_PACKAGES[3].price
    }
  ];

  const meditacionesAddons = [
    {
      id: 'med_musica_original',
      name: 'Música Original Exclusiva',
      price: servicePrices.price_med_musica_original || 16997,
      perProgram: false
    },
    {
      id: 'med_efectos_binaurales',
      name: 'Efectos Binaurales',
      price: servicePrices.price_med_efectos_binaurales || 4997,
      perProgram: false
    }
  ];

  const selectedPkg = meditacionesPackages.find(p => p.id === selectedPackage);
  
  // Calculate addons - some are per program, some are one-time
  const addonsOneTime = selectedAddons.reduce((sum, addonId) => {
    const addon = meditacionesAddons.find(a => a.id === addonId);
    return sum + (addon && !addon.perProgram ? addon.price : 0);
  }, 0);
  
  const addonsPerProgram = selectedAddons.reduce((sum, addonId) => {
    const addon = meditacionesAddons.find(a => a.id === addonId);
    return sum + (addon && addon.perProgram ? addon.price : 0);
  }, 0);

  // Calculate estimated recording hours with factor 2 (for meditaciones)
  // Formula: (quantity × duration_minutes × 2) / 60
  const MEDITACIONES_FACTOR = 2;
  const estimatedRecordingHours = meditacionesData.episodeDuration 
    ? (meditacionesQuantity * meditacionesData.episodeDuration * MEDITACIONES_FACTOR) / 60 
    : 0;

  // Total calculation
  const packageTotal = selectedPkg?.price 
    ? (estimatedRecordingHours > 0 
        ? (selectedPkg.price * estimatedRecordingHours) + (addonsPerProgram * meditacionesQuantity) + addonsOneTime
        : (selectedPkg.price * meditacionesQuantity) + (addonsPerProgram * meditacionesQuantity) + addonsOneTime)
    : 0;
    
  const badge = selectedPackage ? `${formatPrice(packageTotal)}` : null;

  const handleQuantityChange = (value) => {
    const numValue = parseInt(value, 10);
    if (numValue >= 1 && numValue <= 500) {
      onQuantityChange(numValue);
    }
  };

  const handleDataChange = (field, value) => {
    onMeditacionesDataChange({ ...meditacionesData, [field]: value });
  };

  return (
    <CollapsibleSection
      title="Meditaciones Guiadas"
      icon={Moon}
      isOpen={isOpen}
      onToggle={onToggle}
      badge={badge}
    >
      <div className="space-y-4">
        {/* Meditaciones Data Section */}
        <div className="bg-[#1a1f50]/50 rounded-xl p-4 border border-white/10">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Moon className="w-4 h-4 text-[#aa2aff]" />
            Datos de la Meditación
          </h3>
          
          <div className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Título de la Meditación o Serie</Label>
              <Input
                placeholder="Ej: Meditación para Dormir Profundo"
                value={meditacionesData.title || ''}
                onChange={(e) => handleDataChange('title', e.target.value)}
                className="bg-[#080c2d] border-[#272b96] text-white"
              />
            </div>

            {/* Quantity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Cantidad de Meditaciones</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={meditacionesQuantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="bg-[#080c2d] border-[#272b96] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Duración por Meditación (min)</Label>
                <Input
                  type="number"
                  min="1"
                  max="180"
                  placeholder="Ej: 20"
                  value={meditacionesData.episodeDuration || ''}
                  onChange={(e) => handleDataChange('episodeDuration', parseInt(e.target.value) || 0)}
                  className="bg-[#080c2d] border-[#272b96] text-white"
                />
              </div>
            </div>

            {/* Recording Hours Calculation */}
            {meditacionesData.episodeDuration > 0 && (
              <div className="bg-[#080c2d] rounded-lg p-3 border border-[#272b96]/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#aa2aff]" />
                    <span className="text-slate-300 text-sm">Horas de Grabación Estimadas:</span>
                  </div>
                  <span className="text-white font-bold">{estimatedRecordingHours.toFixed(1)} hrs</span>
                </div>
                <p className="text-slate-500 text-xs mt-1">
                  Fórmula: ({meditacionesQuantity} meditaciones × {meditacionesData.episodeDuration} min × 2) ÷ 60
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Package Selection */}
        <div>
          <h3 className="text-white font-semibold mb-3">Selecciona tu Paquete</h3>
          <p className="text-slate-400 text-sm mb-4">
            {estimatedRecordingHours > 0 
              ? `Precio por hora de grabación × ${estimatedRecordingHours.toFixed(1)} horas estimadas`
              : `Precio por meditación × ${meditacionesQuantity} meditaciones`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {meditacionesPackages.map(pkg => (
              <PackageCard
                key={pkg.id || 'none'}
                pkg={pkg}
                isSelected={selectedPackage === pkg.id}
                onSelect={onPackageSelect}
                formatPrice={formatPrice}
                currency={currency}
              />
            ))}
          </div>
        </div>

        {/* Addons */}
        {selectedPackage && (
          <div>
            <h3 className="text-white font-semibold mb-3">Extras Opcionales</h3>
            <div className="space-y-2">
              {meditacionesAddons.map(addon => (
                <label
                  key={addon.id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all",
                    selectedAddons.includes(addon.id)
                      ? "border-[#aa2aff] bg-[#aa2aff]/10"
                      : "border-white/10 hover:border-white/20 bg-white/5"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded border-2 flex items-center justify-center",
                      selectedAddons.includes(addon.id)
                        ? "border-[#aa2aff] bg-[#aa2aff]"
                        : "border-slate-500"
                    )}>
                      {selectedAddons.includes(addon.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-white text-sm">{addon.name}</span>
                  </div>
                  <span className="text-white font-bold text-sm">
                    +{formatPrice(addon.price)} {currency}
                  </span>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={selectedAddons.includes(addon.id)}
                    onChange={() => onAddonToggle(addon.id)}
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Total Summary */}
        {selectedPackage && packageTotal > 0 && (
          <div className="pt-4 border-t border-white/10">
            <div className="flex justify-between items-center text-lg">
              <span className="text-white font-semibold">Subtotal Meditaciones:</span>
              <span className="text-[#aa2aff] font-bold">{formatPrice(packageTotal)} {currency}</span>
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export default MeditacionesSection;
